/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import styled, { withTheme } from '../styled';
import { Info } from '../types';

interface HeaderProps {
	secure?: boolean;
}

const Header = styled('div')<HeaderProps>`
	position: sticky;
	z-index: 9999;
	left: 0;
	top: calc(
		${props => props.theme.app.header.heightS} -
			${props => props.theme.app.header.heightL}
	);
	width: 100%;
	height: ${props => props.theme.app.header.heightL};
	pointer-events: ${props => (props.secure ? 'none' : 'all')};
`;

interface LogoProps {
	scrolled?: boolean;
	secure?: boolean;
}

const Logo = styled('div')<LogoProps>`
	margin-top: 0.4em;
	margin-bottom: 0.4em;
	fill: ${props => props.theme.color.primary};
	transition: all 0.4s cubic-bezier(0.86, 0, 0.07, 1);
	transform: ${props =>
		props.secure && props.scrolled ? 'translate(0, -10vh)' : 'translate(0, 0)'};
	position: relative;

	& > svg {
		display: block;
		width: ${272 / 16}em;
		height: auto;
	}
`;

interface LowerProps {
	scrolled?: boolean;
	secure?: boolean;
}

const Lower = styled('div')<LowerProps>`
	transition: all 0.4s ease;
	margin-bottom: ${props =>
		props.secure && props.scrolled ? '0.014em' : '0.4em'};
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
`;

const Title = styled('div')`
	font-weight: ${props => props.theme.font.weight.medium};
	color: ${props => props.theme.color.primary};
`;

interface InnerProps {
	scrolled?: boolean;
	secure?: boolean;
}

const Inner = styled('div')<InnerProps>`
	box-sizing: border-box;
	display: flex;
	flex-flow: column nowrap;
	transition: all 0.4s ease;
	background-color: ${props =>
		!props.secure
			? props.theme.color.lightgrey
			: props.theme.app.header.backgroundColor ||
			  props.theme.color.ultraLightgrey};
	color: ${props => props.theme.app.header.color};
	height: ${props =>
		!props.secure ? '100vh' : props.theme.app.header.heightL};
	padding-bottom: ${props => (!props.secure ? '54vh' : 0)};
`;

const Container = styled('div')`
	max-width: ${props => props.theme.dimensions.contentMaxWidth};
	margin: 0 auto;
	width: 100%;
	box-sizing: border-box;
	padding-left: ${props => props.theme.dimensions.sidePadding};
	padding-right: ${props => props.theme.dimensions.sidePadding};
	flex: 1 1 auto;
	display: flex;
	flex-flow: column nowrap;
	justify-content: flex-end;
`;

const Actions = styled('div')`
	display: flex;
`;

const EmailContainer = styled('div')`
	max-width: 8em;
	overflow: hidden;
	text-overflow: ellipsis;
	@media (min-width: 40em) {
		max-width: 20em;
	}
`;

const LogoutButton = styled('div')`
	cursor: pointer;
	font-weight: 600;

	&:hover,
	&:focus {
		text-decoration: underline;
	}
`;

export interface AppHeaderProps {
	scrolled: boolean;
	secure: boolean;
	me: Info | null;
	logout: () => void;
}

interface AppHeaderAndRouteProps extends AppHeaderProps, RouteComponentProps {}

const AppHeader: React.FunctionComponent<AppHeaderAndRouteProps> = ({
	children,
	scrolled,
	secure,
	me,
	...props
}) => {
	return (
		<Header {...props}>
			<Inner scrolled={scrolled} secure={secure}>
				<Container>
					<Logo scrolled={scrolled} secure={secure}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 1190.551 175.673"
						>
							<path d="M65.818 26.472c15.331 0 29.197 5.441 31.177 24.258h32.165C125.697 15.59 100.951 0 66.313 0 28.702 0 4.954 19.302 4.954 46.508c0 24.746 13.353 41.567 40.823 48.748l32.906 8.653c15.09 3.969 22.514 10.641 22.514 22.519 0 14.595-14.595 22.77-33.402 22.77-19.79 0-32.911-7.678-35.626-27.47H0c2.971 39.094 30.931 53.945 68.288 53.945 38.599 0 65.071-18.563 65.071-49.481 0-27.714-14.843-42.072-43.548-49.497l-28.699-7.417c-16.077-4.21-23.999-10.149-23.999-23.001.001-12.134 10.893-19.805 28.705-19.805m234.656 61.37C300.474 30.431 273.004 0 225.505 0c-47.506 0-74.976 30.431-74.976 87.842 0 57.391 27.47 87.832 74.976 87.832 47.499-.001 74.969-30.442 74.969-87.832m-74.969 60.625c-22.265 0-40.336-12.388-40.336-34.653v-51.96c0-22.26 18.071-34.641 40.336-34.641s40.331 12.381 40.331 34.641v51.96c0 22.265-18.066 34.653-40.331 34.653m183.931-12.634L376.773 3.712h-52.447v168.239h30.928V38.596h.988l34.384 133.355h38.353l34.397-133.355h.988v133.355h30.931V3.712h-52.701l-32.168 132.121h-.99zm218.573-3.954L577.789 3.712h-48.26v168.239h31.924V43.548h.988l50.22 128.403h48.255V3.712h-31.919v128.167h-.988zm66.402 40.072h32.17V3.712h-32.17v168.239zm166.375 0h34.641L838.267 3.712h-34.143l-57.401 168.239h34.643l10.88-35.872h57.403l11.137 35.872zM821.944 42.555l19.548 66.318h-40.826l20.282-66.318h.996zM972.97 175.673c35.131 0 61.359-16.578 66.554-55.187h-32.167c-3.214 19.805-13.853 27.981-33.889 27.981-20.287 0-36.38-14.859-36.38-35.151V61.854c0-21.031 14.851-34.641 35.392-34.641 19.053 0 30.421 8.912 32.16 26.729h32.17C1034.571 20.787 1009.34 0 974.699 0c-48.737 0-72.239 34.884-72.239 88.332 0 50.718 22.009 87.341 70.51 87.341m150.032-149.201c15.346 0 29.197 5.441 31.19 24.258h32.157C1182.883 15.59 1158.148 0 1123.497 0c-37.609 0-61.344 19.302-61.344 46.508 0 24.746 13.353 41.567 40.813 48.748l32.906 8.653c15.095 3.969 22.519 10.641 22.519 22.519 0 14.595-14.602 22.77-33.396 22.77-19.805 0-32.911-7.678-35.628-27.47H1057.2c2.961 39.094 30.926 53.945 68.283 53.945 38.596 0 65.069-18.563 65.069-49.481 0-27.714-14.838-42.072-43.548-49.497l-28.702-7.417c-16.083-4.21-23.994-10.149-23.994-23.001 0-12.134 10.882-19.805 28.694-19.805" />
						</svg>
					</Logo>
					<Lower scrolled={scrolled} secure={secure}>
						<Title>birdly.nest{!secure && ' – Login'}</Title>
						<Actions>
							{secure && (
								<React.Fragment>
									<EmailContainer>{me ? me.user.email : ''}</EmailContainer>
									<div style={{ margin: '0 0.3em' }}>–</div>
									<LogoutButton role="button" onClick={props.logout}>
										Logout
									</LogoutButton>
								</React.Fragment>
							)}
						</Actions>
					</Lower>
				</Container>
			</Inner>
		</Header>
	);
};

export default withRouter(withTheme(AppHeader));
